import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BrandingConfig, BrandingApiResponse } from '../../theme/types';
import { defaultBranding, generateDerivedColors } from '../../theme/defaultTheme';

interface BrandingState {
  branding: BrandingConfig;
  isLoading: boolean;
  isCustomBranding: boolean;
  isBackendReady: boolean;
  error: string | null;
}

const initialState: BrandingState = {
  branding: defaultBranding,
  isLoading: true,
  isCustomBranding: false,
  isBackendReady: false,
  error: null,
};

/**
 * Merge API response with default branding
 */
function mergeBrandingConfig(apiConfig: BrandingApiResponse): BrandingConfig {
  const derivedColors = apiConfig.primaryColor
    ? generateDerivedColors(apiConfig.primaryColor)
    : {};

  return {
    ...defaultBranding,
    ...derivedColors,
    tenantName: apiConfig.tenantName ?? defaultBranding.tenantName,
    appTitle: apiConfig.appTitle ?? defaultBranding.appTitle,
    tagline: apiConfig.tagline ?? defaultBranding.tagline,
    logoUrl: apiConfig.logoUrl ?? defaultBranding.logoUrl,
    primaryColor: apiConfig.primaryColor ?? defaultBranding.primaryColor,
    secondaryColor: apiConfig.secondaryColor ?? derivedColors.secondaryColor ?? defaultBranding.secondaryColor,
    backgroundColor: apiConfig.backgroundColor ?? defaultBranding.backgroundColor,
    textColor: apiConfig.textColor ?? defaultBranding.textColor,
    fontFamily: apiConfig.fontFamily ?? defaultBranding.fontFamily,
  };
}

const brandingSlice = createSlice({
  name: 'branding',
  initialState,
  reducers: {
    // Triggered when user authenticates (Firebase auth state change)
    userAuthenticated: (state, action: PayloadAction<{ uid: string; email: string | null; displayName: string | null }>) => {
      state.isLoading = true;
      state.error = null;
    },

    // Backend registration successful
    backendAuthSuccess: (state) => {
      state.isBackendReady = true;
    },

    // Backend registration failed
    backendAuthFailure: (state, action: PayloadAction<string>) => {
      state.isBackendReady = false;
      state.error = action.payload;
      state.isLoading = false;
    },

    // Fetch branding request
    fetchBrandingRequest: (state) => {
      state.isLoading = true;
      state.error = null;
    },

    // Fetch branding success
    fetchBrandingSuccess: (state, action: PayloadAction<BrandingApiResponse | null>) => {
      if (action.payload && Object.keys(action.payload).length > 0) {
        state.branding = mergeBrandingConfig(action.payload);
        state.isCustomBranding = true;
      } else {
        state.branding = defaultBranding;
        state.isCustomBranding = false;
      }
      state.isLoading = false;
      state.error = null;
    },

    // Fetch branding failure
    fetchBrandingFailure: (state, action: PayloadAction<string>) => {
      state.branding = defaultBranding;
      state.isCustomBranding = false;
      state.isLoading = false;
      state.error = action.payload;
    },

    // User logged out - reset everything
    userLoggedOut: (state) => {
      state.branding = defaultBranding;
      state.isLoading = false;
      state.isCustomBranding = false;
      state.isBackendReady = false;
      state.error = null;
    },

    // Reset branding state
    resetBranding: () => initialState,
  },
});

export const {
  userAuthenticated,
  backendAuthSuccess,
  backendAuthFailure,
  fetchBrandingRequest,
  fetchBrandingSuccess,
  fetchBrandingFailure,
  userLoggedOut,
  resetBranding,
} = brandingSlice.actions;

export default brandingSlice.reducer;

// Selectors
export const selectBranding = (state: { branding: BrandingState }) => state.branding.branding;
export const selectBrandingIsLoading = (state: { branding: BrandingState }) => state.branding.isLoading;
export const selectIsCustomBranding = (state: { branding: BrandingState }) => state.branding.isCustomBranding;
export const selectIsBackendReady = (state: { branding: BrandingState }) => state.branding.isBackendReady;
export const selectBrandingError = (state: { branding: BrandingState }) => state.branding.error;
